import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * The online-payment status card — plan-005 OP-38 (FR-MNY-6; OP-32's `GET /v1/online-payment/status`).
 *
 * One component, three places: the Payouts page, the new-agent wizard's "How callers pay" and the
 * voice agent's Payments setting. It says, in this order:
 *
 *   1. the account's mode (only WeTalk sets it) and whether callers are offered online payment NOW;
 *   2. what stands in the way, one line each, in the order someone has to act — the API's `missing`
 *      codes rendered as words, never as codes;
 *   3. the terms: WeTalk absorbs the processing fee and takes no commission (`fee_note` is a code;
 *      the words are {@link FEE_NOTE_TEXT}'s), the holding period and the payout schedule, the
 *      minimum, and the 7-day hold after the payout details change.
 *
 * Choosing "online" is allowed whatever the status says: accepting it is the shop's choice, and a
 * caller is offered it only while it is switched on (design §18.11.1). The card is what makes that
 * honest — the shop sees, beside the choice, whether it does anything yet.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Tag } from "@wetalk/ui";
import type { ReactNode } from "react";

import { useGateway } from "../../api/provider.js";
import { apiProblemText } from "../../api/api-error.js";
import {
  ONLINE_PAYMENT_MISSING_TEXT,
  ONLINE_PAYMENT_MODE_LABEL,
  ONLINE_PAYMENT_MODE_TONE,
  onlinePaymentHeadlineOf,
  onlinePaymentTermsOf,
  type OnlinePaymentStatusView,
} from "../../api/slice/payout.js";
import { formatMoney } from "../../money.js";
import { onlinePaymentStatusQuery } from "./payout-query.js";

import "./payouts.css";

/** The plan's own sentence (OP-38 Task 2), shown while nobody is offered online payment yet. */
export function ONLINE_PAYMENT_AVAILABLE_AFTER(): string { return portalText("finance", "m1205"); }

export interface OnlinePaymentStatusBodyProps {
  readonly status: OnlinePaymentStatusView;
  /** Offer the way to the Payouts page when payout details are missing (not on the Payouts page itself). */
  readonly linkToPayouts: boolean;
}

/** The card's body, from a status already read — the Payouts page reads it once for several parts. */
export function OnlinePaymentStatusBody({ status, linkToPayouts }: OnlinePaymentStatusBodyProps): ReactNode {
  usePortalTranslation("finance");
  const nobodyOffered = !status.offeredToCallers && !status.offeredInWebChat;
  const needsDestination = status.missing.includes("destination");
  return (
    <>
      <span className="po-status-head">
        <span className="po-status-title">{portalText("finance", "m0763")}</span>
        <Tag status={ONLINE_PAYMENT_MODE_TONE[status.mode]}>{ONLINE_PAYMENT_MODE_LABEL[status.mode]}</Tag>
      </span>
      <span className="po-status-headline">{onlinePaymentHeadlineOf(status)}</span>
      {status.missing.length === 0 ? null : (
        <span className="po-status-missing">
          {status.missing.map((one) => (
            <span key={one} className="po-status-missing-item">
              {ONLINE_PAYMENT_MISSING_TEXT[one]}
            </span>
          ))}
        </span>
      )}
      {nobodyOffered ? <span className="po-status-note">{ONLINE_PAYMENT_AVAILABLE_AFTER()}</span> : null}
      {needsDestination && linkToPayouts ? (
        <span className="po-status-note">
          <Link to="/payouts" className="po-link">{portalText("finance", "m0764")}</Link>
        </span>
      ) : null}
      <span className="po-status-terms">
        {onlinePaymentTermsOf(status, formatMoney).map((one) => (
          <span key={one} className="po-status-term">
            {one}
          </span>
        ))}
      </span>
    </>
  );
}

/** The card, reading the status itself — for the wizard and the Payments setting. */
export function OnlinePaymentStatusCard({ linkToPayouts }: { readonly linkToPayouts: boolean }): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const status = useQuery(onlinePaymentStatusQuery(gateway));
  return (
    <span className="po-status" role="group" aria-label={portalText("finance", "m0765")}>
      {status.data !== undefined ? (
        <OnlinePaymentStatusBody status={status.data} linkToPayouts={linkToPayouts} />
      ) : status.isError ? (
        <span className="po-status-headline" role="alert">
          {apiProblemText(status.error, portalText("finance", "m0766"))}
        </span>
      ) : (
        <span className="po-status-headline" role="status">{portalText("finance", "m0767")}</span>
      )}
    </span>
  );
}
